import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { PurchasePopups } from '../components/PurchasePopups'
import { api, formatVnd, type HomeData, type Review } from '../lib/api'

const ASSET = {
  /** Kỳ quan / cảnh quan mới — Chase the Skies (khác Wilderness Bound ở trang key) */
  hero: '/image/minecraft-wonder-chase.jpg',
  /** Minecraft Launcher bản mới (3.36.x) */
  launcher: '/image/minecraft-launcher-latest.png',
  java: '/image/minecraft-wonder-copper.png',
  payMethods:
    'https://accminecraft.net/image/homepage2/cac-phuong-thuc-thanh-toan-khi-mua-minecraft.png',
  trust: 'https://i.imgur.com/3qwUL6s.png',
  keyImg: '/image/minecraft-key-product.jpg',
  blogCover: '/image/minecraft-launcher-java.png',
  youtube: 'https://www.youtube.com/embed/atd7BVU7G4o?si=vrf-hojRn9PxVAyU',
}

function Stars({ avg, count }: { avg: number; count: number }) {
  const full = Math.floor(avg)
  const half = avg - full >= 0.5
  return (
    <p className="home-stars" aria-label={`${avg} trên 5 sao, ${count} đánh giá`}>
      {Array.from({ length: 5 }, (_, i) => {
        const n = i + 1
        const filled = n <= full || (n === full + 1 && half)
        return (
          <i
            key={n}
            className={`fa-${filled ? 'solid' : 'regular'} fa-star star${filled ? ' on' : ''}`}
            aria-hidden
          />
        )
      })}
      <strong>{avg.toFixed(1).replace('.', ',')}</strong>/5 · {count.toLocaleString('vi-VN')} đánh
      giá
    </p>
  )
}

function ReviewCard({ r }: { r: Review }) {
  return (
    <article className="card home-review-card">
      <p>
        <b>{r.user_name}:</b> {r.content}
      </p>
      <p>
        Đánh giá:{' '}
        {Array.from({ length: Math.min(r.stars, 5) }, (_, i) => (
          <i key={i} className="fa-solid fa-star star on" aria-hidden />
        ))}
      </p>
      <p className="muted">
        Ngày mua:{' '}
        {r.created_at
          ? new Date(r.created_at).toLocaleDateString('vi-VN')
          : '—'}
      </p>
    </article>
  )
}

export function HomePage() {
  const [data, setData] = useState<HomeData | null>(null)
  const [err, setErr] = useState('')

  useEffect(() => {
    api
      .home()
      .then((r) => setData(r.data))
      .catch((e) => setErr(e.message))
  }, [])

  if (err) return <p className="note">{err}</p>
  if (!data) return <p className="muted">Đang tải…</p>

  const key = data.key_product
  const avg = data.reviews.stats.avg
  const count = data.reviews.stats.count
  const keySlug = '/mua-minecraft'
  const priceCk = key?.price_transfer ?? 640000

  return (
    <div className="home-prod">
      {data.recent_purchases?.length ? (
        <PurchasePopups items={data.recent_purchases} />
      ) : null}

      <section className="home-card">
        <h1>Mua Minecraft Java &amp; Bedrock cho PC — Accminecraft.net</h1>
        {count > 0 ? <Stars avg={avg} count={count} /> : null}

        <div className="home-policy">
          <h2>Thông báo về chính sách bán hàng từ 2026</h2>
          <p>
            Accminecraft.net là nơi cung cấp tài khoản đã qua sử dụng của trò chơi Minecraft kể từ
            2017, giúp cho hàng ngàn người có bản quyền Minecraft giá rẻ phù hợp với túi tiền của học
            sinh, sinh viên.
          </p>
          <p>
            Shop mua và bán lại những tài khoản Minecraft thu mua từ người chơi đã chán game trong
            cộng đồng và bán lại cho những người cần chúng.
          </p>
          <p>
            Tuy nhiên từ 2026 việc này không còn dễ dàng bởi vì Microsoft đã nâng cấp nhiều tính năng
            hỗ trợ việc thu hồi lại tài khoản đã mất. Vì vậy người bán acc cho chúng tôi họ dễ dàng
            lấy lại tài khoản thông qua <strong>Windows Hello Code</strong> hoặc thông tin gốc của
            tài khoản.
          </p>
          <div className="home-policy-warn">
            <p>
              <strong>Do đó các câu hỏi liên quan việc:</strong>
            </p>
            <ul>
              <li>Shop có còn bán acc nữa không?</li>
              <li>Có acc Minecraft nào giá rẻ không?</li>
            </ul>
            <p>
              <strong>Sẽ không còn được hỗ trợ.</strong> Vui lòng mua{' '}
              <strong>CD KEY Minecraft</strong> và nhập vào tài khoản Microsoft cá nhân của bạn để sở
              hữu bản quyền game một cách chính thức và an toàn nhất!
            </p>
          </div>
          <p>
            Các sản phẩm khác như đặt hộ rank Hypixel, Lunar Store,.. vẫn còn — bạn đặt hàng sẽ giao
            trong <strong>12 tiếng</strong>.
          </p>
          <p>Hãy nhắn cho mình qua <strong>Zalo</strong> nếu bạn cần mua gì đó.</p>
          <p className="home-center">
            <Link className="btn btn-primary" to={keySlug}>
              <i className="fa-solid fa-cart-shopping" aria-hidden />{' '}
              Mua CD KEY Minecraft
            </Link>
          </p>
        </div>

        <h2 className="home-h2">Hướng dẫn và hỗ trợ sau khi mua Minecraft</h2>
        <ul className="home-guide">
          <li>Video hướng dẫn mua Minecraft và thanh toán trên website.</li>
          <li>Video cài đặt, đăng nhập và chơi Minecraft Java &amp; Bedrock trên PC.</li>
          <li>Giá, phương thức thanh toán và đánh giá người mua hiển thị rõ.</li>
          <li>Hỗ trợ nhanh khi kích hoạt key hoặc gặp lỗi tài khoản Microsoft.</li>
        </ul>
      </section>

      <section className="home-block home-center">
        <h2>Mua Minecraft Java &amp; Bedrock — bản quyền Microsoft cho PC</h2>
        <h3>Mua Minecraft Premium tại Accminecraft.net</h3>
        <figure className="home-figure">
          <img src={ASSET.hero} alt="Accminecraft.net" />
        </figure>
      </section>

      <section className="home-block home-center">
        <h3>QUÁ TRÌNH MUA MINECRAFT BẢN QUYỀN TẠI ACCMINECRAFT.NET</h3>
        <div className="home-video">
          <iframe
            src={ASSET.youtube}
            title="YouTube video player"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
        <Link className="btn btn-primary home-mt" to={keySlug}>
          <i className="fa-solid fa-key" aria-hidden /> Mua bản quyền Minecraft PC
        </Link>
      </section>

      <section className="home-split">
        <div>
          <h3>TRÌNH KHỞI CHẠY</h3>
          <p>
            Minecraft Launcher là nơi khởi chạy mọi phiên bản trò chơi Minecraft chính thức như bản
            Minecraft Java Edition hay Bedrock Edition. Trên Launcher còn 2 game khác như Minecraft
            Dungeons và Minecraft Legends.
          </p>
          <a
            className="btn btn-ghost"
            href="https://accminecraft.net/tai-game-minecraft-mien-phi"
            target="_blank"
            rel="noreferrer"
          >
            TẢI NGAY
          </a>
        </div>
        <img src={ASSET.keyImg} alt="Trình khởi chạy Minecraft Launcher miễn phí" />
      </section>

      <section className="home-split">
        <div>
          <h3>MINECRAFT JAVA &amp; BEDROCK EDITION</h3>
          <p>
            Minecraft Premium (Minecraft Java &amp; Bedrock Edition) là bản chơi Minecraft trên PC
            Windows / MacOs / Linux đầy đủ nhất.
            <br />
            Game có cộng đồng người chơi vô cùng lớn và nhiều mod để bạn khám phá. Ngoài ra còn có thể
            chơi được các server bản quyền lớn, nổi tiếng như HYPIXEL / Mineplex / Pixelmon /
            Minescape ..
          </p>
          <Link className="btn btn-ghost" to={keySlug}>
            MUA NGAY {formatVnd(priceCk)}
          </Link>
        </div>
        <img src={ASSET.java} alt="Minecraft Java Edition bản trả phí giá rẻ" />
      </section>

      <section className="home-block home-center">
        <h3>
          CÁCH ĐỂ SỞ HỮU BẢN QUYỀN MINECRAFT TẠI{' '}
          <Link to="/">ACCMINECRAFT.NET</Link>
        </h3>
        <div className="home-key-card panel">
          <p>
            ✔️ Mua bản quyền chính hãng, chính chủ bằng Key (chìa khóa kích hoạt) dùng để đổi lấy game
            bản quyền trên chính tài khoản Microsoft của bạn.
            <br />
            <b>Bằng cách này bạn sẽ có cả 2 phiên bản chơi trên PC là Java Edition &amp; Bedrock Edition</b>
          </p>
          <Link to={keySlug} className="home-key-product">
            <img src={ASSET.keyImg} alt="KEY MINECRAFT JAVA & BEDROCK PC" />
            <div>
              <h4>KEY MINECRAFT JAVA &amp; BEDROCK PC</h4>
              <p>
                <b className="price-hot">{formatVnd(priceCk)}</b>{' '}
                <s>670.000đ</s>
              </p>
            </div>
          </Link>
        </div>
      </section>

      <section className="home-block">
        <h3 className="home-center">KHÁCH HÀNG NÓI GÌ VỀ ACCMINECRAFT.NET?</h3>
        <p className="home-center home-lead">
          Sau khi mua bất kì sản phẩm nào dù sản phẩm đó có giá 0 đồng tại Accminecraft.net khách hàng
          đều có thể để lại đánh giá về trải nghiệm của họ tại mục [ĐÁNH GIÁ SHOP] trong [ĐƠN HÀNG ĐÃ
          MUA] và dưới đây là những đánh giá mới nhất từ khách hàng mà bạn có thể xem được nội dung,
          số sao đánh giá và phản hồi từ shop Minecraft. Cảm ơn bạn đã ghé thăm Accminecraft.net và
          hi vọng chúng tôi sẽ có cơ hội phục vụ bạn!!!!!!
        </p>
        <p className="home-center">
          <img src={ASSET.trust} alt="" className="home-trust-img" />
        </p>
        <div className="home-stats">
          <div>
            <strong>{(data.stats?.reviews ?? count).toLocaleString('vi-VN')}</strong>
            <span>đánh giá</span>
          </div>
          <div>
            <strong>{(data.stats?.transactions ?? 0).toLocaleString('vi-VN')}</strong>
            <span>đơn hàng</span>
          </div>
          <div>
            <strong>{(data.stats?.categories ?? 0).toLocaleString('vi-VN')}</strong>
            <span>sản phẩm đã giới thiệu</span>
          </div>
          <div>
            <strong>{(data.stats?.items_delivered ?? 0).toLocaleString('vi-VN')}</strong>
            <span>vật phẩm đã giao</span>
          </div>
        </div>
        <div className="home-review-grid">
          {data.reviews.items.map((r) => (
            <ReviewCard key={r.id} r={r} />
          ))}
        </div>
        <p className="home-center home-mt">
          <Link to="/reviews">
            <i className="fa-solid fa-arrow-right" aria-hidden /> XEM TOÀN BỘ ĐÁNH GIÁ TẠI ĐÂY
          </Link>
        </p>
      </section>

      {data.blogs?.length ? (
        <section className="home-block">
          <h3 className="home-center">BÀI VIẾT MỚI</h3>
          <div className="blog-grid home-blog-grid">
            {data.blogs.slice(0, 3).map((b, i) => (
              <Link key={b.id} className="blog-card card-link" to={`/blog/${b.slug}`}>
                <div className="blog-card-media">
                  <img
                    src={
                      b.image_url ||
                      (i % 2 === 0 ? ASSET.launcher : ASSET.blogCover)
                    }
                    alt=""
                    loading="lazy"
                  />
                </div>
                <div className="blog-card-body">
                  <h3>{b.title}</h3>
                  <p>{b.excerpt?.replace(/<[^>]+>/g, '').slice(0, 100)}</p>
                </div>
              </Link>
            ))}
          </div>
          <p className="home-center home-mt">
            <Link to="/blog">
              <i className="fa-solid fa-newspaper" aria-hidden /> Xem tất cả bài viết
            </Link>
          </p>
        </section>
      ) : null}

      <section className="home-pay">
        <h3 className="home-center">PHƯƠNG THỨC THANH TOÁN</h3>
        <div className="home-pay-grid">
          <img src={ASSET.payMethods} alt="Phương thức thanh toán khi mua Minecraft" />
          <ul>
            <li>
              Cách 1 <b>thanh toán bằng thẻ cào</b> khi nạp vào shop sẽ không tính phí đổi thẻ. Ví dụ
              bạn nạp 10.000đ thẻ cào sẽ quy ra 10.000 xu để mua sản phẩm. Vd: sản phẩm 40.000 xu thì
              cần nạp 40.000 thẻ.
              <br />
              Chấp nhận các loại thẻ: Viettel, Mobifone, Vinaphone, Vietnamobile, Garena, Zing..
            </li>
            <li>
              Cách 2: thanh toán tự động qua ATM / MOMO các ngân hàng tại Việt Nam
              <br />
              Trong mỗi sản phẩm bạn xem đều có nút <b>CHUYỂN KHOẢN TỰ ĐỘNG</b>
              <br />
              Bạn thanh toán cho đơn hàng của mình theo hướng dẫn và bạn sẽ nhận được key trong vòng
              1 phút qua email và trên giao diện website.
            </li>
            <li className="home-zalo-tip">
              Bạn cần tư vấn hãy chat Zalo ở góc phải màn hình (admin 24/7)
            </li>
          </ul>
        </div>
      </section>

      <section className="home-summary">
        <h3>Đánh giá từ khách hàng</h3>
        {count > 0 ? (
          <>
            <div className="home-summary-stars" aria-hidden>
              {Array.from({ length: Math.min(5, Math.round(avg)) }, (_, i) => (
                <i key={i} className="fa-solid fa-star" />
              ))}
            </div>
            <p className="home-summary-score">
              {avg.toFixed(1).replace('.', ',')}
              <span>/5</span>
            </p>
            <p>
              Dựa trên <strong>{count.toLocaleString('vi-VN')}</strong> đánh giá đã hiển thị
            </p>
          </>
        ) : (
          <p>Chưa có đánh giá đã duyệt để thống kê. Hãy là người đầu tiên sau khi mua hàng!</p>
        )}
        <Link className="btn btn-ghost home-mt home-summary-btn" to="/reviews">
          <i className="fa-solid fa-star" aria-hidden /> Xem tất cả đánh giá
        </Link>
      </section>
    </div>
  )
}
